/* eslint-disable prefer-destructuring */
/* eslint-disable max-lines-per-function */
/* eslint-disable one-var */
/* eslint-disable max-depth */
import { throttle } from 'lodash-es';
import { CONFIG_ATTR } from './exposure-core';
import { BlockConfigParams, CompShowElement } from './type/btm-block';
import { findParentWithAttribute, getFullBtmDPoint, getXpath, shouldReportClick } from './util/pages';
import { findNearestAttribute, isEmptyBtmC, isEmptyBtmD } from './util/exposure';
import BtmCore from './btm-core';
import { BTMEventType, RemoteConfig } from './type/btm-core';
import { getBtmCode } from './util/actionBtm';
import {
  checkHTMLTag,
  checkM4B,
  checkSVG,
  findMatchingSelector,
  getAutoBtm,
  getClassName,
  getCssSelector,
  getDeepestTextNode,
  getLatestDataUid,
  getStarlingKey
} from './util/full-track';

export default class ModelNodesManager {
  /** btm核心 */
  BtmCore?: BtmCore;
  /** 存储 btm 点位曝光相关信息，key值为btm */
  observePool: Map<string, CompShowElement[]> = new Map();

  exposeElements: HTMLElement[] = [];

  /** 各个点位曝光节流 */
  private readonly throttlePool: Map<string, (args: any) => void> = new Map();

  /** 各个点位曝光结束节流 */
  private readonly throttleEndPool: Map<string, (args: any) => void> = new Map();

  /** 初始化 blockConfig */
  private blockConfig: BlockConfigParams = {};

  private remoteConfig?: RemoteConfig;

  init(core: BtmCore, blockConfig: BlockConfigParams, remoteConfig?: RemoteConfig): void {
    this.BtmCore = core;
    this.blockConfig = blockConfig;
    this.remoteConfig = remoteConfig;
  }

  getObservedElement(node: HTMLElement, btm: string) {
    const btmObjList = this.observePool.get(btm);
    if (!btmObjList) return;
    const xpath = getXpath(node);
    const btmObj = btmObjList.find((item) => item.xpath === xpath);
    return btmObj ? btmObj : btmObjList[0];
  }

  /**
   * 判断是否达允许上报
   * @param node 要曝光的元素
   * @param intersectionRatio 目前曝光百分比
   * @param isInOverlap 是否是覆盖计算的情况
   * @returns 是否达到曝光比例要求
   */
  checkExposureStatus(node: HTMLElement, intersectionRatio: number, isInOverlap?: boolean): boolean {
    const { btm } = this.BtmCore!.getFullBtmInfo(node);

    // 如果没有btm信息则不上报
    if (isEmptyBtmC(btm)) {
      return false;
    }
    const observedElement = this.getObservedElement(node, btm);
    if (observedElement) {
      const { percent, once, isReported } = observedElement;
      // 曝光比例不够，则不上报
      // 如果是覆盖计算完返回0，则是被完全覆盖了，则不上报
      if (intersectionRatio < percent || (isInOverlap && intersectionRatio === 0)) {
        return false;
      }
      // 只要曝光一次，曝光过了,就不曝光了
      if (once && isReported) {
        return false;
      }
    }
    return true;
  }

  checkIsExposed(node: HTMLElement) {
    const { btm } = this.BtmCore!.getFullBtmInfo(node);

    // 如果没有btm信息则不上报
    if (isEmptyBtmC(btm)) {
      return false;
    }
    const observedElement = this.getObservedElement(node, btm);
    const config = JSON.parse(node?.getAttribute(CONFIG_ATTR) || '{}');
    let shouldReportDuration;
    if (typeof config.reportDuration !== 'undefined') {
      shouldReportDuration = config.reportDuration;
    } else {
      const btmC = getBtmCode(btm, 'c') || 'c0';
      shouldReportDuration = this.remoteConfig?.moduleConfig?.enableModuleDuration?.includes(btmC);
    }
    if (shouldReportDuration && observedElement) {
      const { isReported, once, reportTimeStamp, isEndReported } = observedElement;
      if (isReported && reportTimeStamp && !isEndReported) {
        return true;
      }
      if (isReported && isEndReported) {
        if (once) {
          return false;
        } else {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * 进行曝光上报
   * @param node 要曝光的元素
   * @returns 是否需要继续监听曝光
   */
  expose(node: HTMLElement): boolean {
    try {
      const { btm } = this.BtmCore!.getFullBtmInfo(node);
      const btmD = getFullBtmDPoint(btm);
      if (!isEmptyBtmC(btm) && btmD === 'd0') {
        const observedElement = this.getObservedElement(node, btm);
        // 观察池没有该元素直接返回
        if (!observedElement) {
          return false;
        }
        const { once, useBeacon } = observedElement;
        const now = new Date().getTime();
        observedElement.isReported = true;
        observedElement.reportTimeStamp = now;
        observedElement.isEndReported = false;
        this.exposeElements.push(node);
        let throttleFunc = this.throttlePool.get(btm);
        if (throttleFunc) {
          throttleFunc(observedElement);
        } else {
          throttleFunc = throttle((param) => {
            // 上报
            const { param: _params, ...blockInfo } = param;
            delete blockInfo.xpath;
            this.BtmCore?.btmReport({
              eventName: BTMEventType.ModuleShow,
              eventParams: {
                ...this.BtmCore?.btmInfo,
                ...blockInfo,
                btm_block_params: { ...this.BtmCore?.BCMContext, ...param?.params }
              },
              time: new Date().getTime(),
              useBeacon
            });
          }, observedElement?.throttleTime || 0);
          this.throttlePool.set(btm, throttleFunc);
          throttleFunc(observedElement);
        }

        // 如果只一次，返回false，不再监听
        if (once) {
          return false;
        }
      }
      return true;
    } catch (error) {
      console.error('BTM WEB SDK：曝光上报失败。', error);
      // 没能成功曝光，就继续监听它
      return true;
    }
  }

  /**
   * 进行曝光上报
   * @param node 要曝光的元素
   * @returns 是否需要继续监听曝光
   */
  exposeDuration(node: HTMLElement): boolean {
    try {
      const { btm } = this.BtmCore!.getFullBtmInfo(node);
      if (!isEmptyBtmC(btm)) {
        const observedElement = this.getObservedElement(node, btm);
        // 观察池没有该元素直接返回
        if (!observedElement) {
          return false;
        }
        const { once, useBeacon } = observedElement;
        let throttleFunc = this.throttleEndPool.get(btm);
        if (throttleFunc) {
          throttleFunc(observedElement);
        } else {
          throttleFunc = throttle((param) => {
            // 上报
            const { param: _params, reportTimeStamp, ...blockInfo } = param;
            delete blockInfo.xpath;

            if (reportTimeStamp) {
              const dateNow = new Date().getTime();
              this.BtmCore?.btmReport({
                eventName: BTMEventType.ModuleDisappear,
                eventParams: {
                  ...this.BtmCore?.btmInfo,
                  ...blockInfo,
                  btm_block_params: { ...this.BtmCore?.BCMContext, ...param?.params },
                  duration: dateNow - reportTimeStamp
                },
                time: dateNow,
                useBeacon
              });
              observedElement.isEndReported = true;
              this.exposeElements = this.exposeElements.filter((element) => element !== node);
            }
          }, observedElement?.throttleTime || 0);
          this.throttleEndPool.set(btm, throttleFunc);
          throttleFunc(observedElement);
        }

        // 如果只一次，返回false，不再监听
        if (once) {
          return false;
        }
      }
      return true;
    } catch (error) {
      console.error('BTM WEB SDK：曝光上报失败。', error);
      // 没能成功曝光，就继续监听它
      return true;
    }
  }

  /**
   * 存储需要被观察的元素
   * @param node 要被观察的元素
   * @returns 该点位是否还没有被观察了
   */
  addObserveNode(node?: HTMLElement) {
    try {
      const { btm } = this.BtmCore!.getFullBtmInfo(node);
      if (!isEmptyBtmC(btm)) {
        // 若是新节点，加入观察池
        const { once, percent, throttleTime, params, useBeacon } = JSON.parse(
          node?.getAttribute(CONFIG_ATTR) || '{}'
        );
        const xpath = getXpath(node) || 'null';
        const parentBCM = findParentWithAttribute(node);
        const attributeList = this.observePool.get(btm);
        if (!attributeList) {
          this.observePool.set(btm, [
            {
              btm,
              once: once || this.blockConfig?.once,
              percent: percent || this.blockConfig?.percent,
              throttleTime: throttleTime || this.blockConfig?.throttleTime,
              params: { ...parentBCM, ...params },
              isReported: false,
              isEndReported: false,
              xpath,
              useBeacon
            }
          ]);
        } else {
          const res = {
            btm,
            once: once || this.blockConfig?.once,
            percent: percent || this.blockConfig?.percent,
            throttleTime: throttleTime || this.blockConfig?.throttleTime,
            params: { ...parentBCM, ...params },
            isReported: false,
            isEndReported: false,
            xpath,
            useBeacon
          };

          let found = false;
          attributeList.forEach((item, index, array) => {
            if (item.xpath === res.xpath) {
              array[index] = res;
              found = true;
            }
          });
          // 如果没有找到，添加新对象
          if (!found) {
            attributeList.push(res);
          }
          this.observePool.set(btm, attributeList);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('BTM WEB SDK：点击上报失败。', error);
      return false;
    }
  }

  /**
   * 判断是否达允许上报点击，并进行上报处理
   * @param node 被点击的元素
   */
  checkAndHandleClickStatus(node: HTMLElement) {
    this.reportFullTrackEvent(node);
    try {
      const { btm } = this.BtmCore!.getFullBtmInfo(node);
      const eleBlockConfig = JSON.parse(findNearestAttribute(node, CONFIG_ATTR) || '{}');
      let shouldReport;
      if (typeof eleBlockConfig.closeClick !== 'undefined') {
        shouldReport = !eleBlockConfig.closeClick;
      } else {
        const btmD = getFullBtmDPoint(btm) || 'd0';
        const disableArr = this.remoteConfig?.locationConfig?.disableBlockClick;
        shouldReport = shouldReportClick(btmD, disableArr);
      }
      if (!isEmptyBtmD(btm) && shouldReport) {
        const param: any = { ...this.BtmCore?.btmInfo, btm };
        const { params, useBeacon } = eleBlockConfig;
        const { param: _params, ...blockInfo } = param;
        const parentBCM = findParentWithAttribute(node);
        // 上报
        this.BtmCore?.btmReport({
          eventName: BTMEventType.Click,
          eventParams: {
            ...blockInfo,
            btm_block_params: { ...this.BtmCore.BCMContext, ...parentBCM, ...params }
          },
          time: new Date().getTime(),
          useBeacon
        });
      }
    } catch (error) {
      console.error('BTM WEB SDK：点击上报失败。', error);
    }
  }

  reportFullTrackEvent(node: HTMLElement) {
    // 根据白名单原则开启全埋点
    const currentBId = this.BtmCore?.getBId();
    if (
      !currentBId ||
      !this.remoteConfig?.pageConfig?.enableFullTracking ||
      !this.remoteConfig?.pageConfig?.enableFullTracking.includes(currentBId)
    ) {
      return;
    }

    try {
      let svgClassName, finalTarget, curSelector, text;

      // 1. 是否 match selector，是否开启了开关
      const selectorInfo = this.remoteConfig?.pageConfig?.positions?.find(
        (item) => item.btmCode === currentBId
      );
      const selectors = selectorInfo?.selectors;
      if (selectors && selectors.length > 0) {
        const result = findMatchingSelector(node, selectors);
        if (result) {
          curSelector = result.matchedSelector;
          finalTarget = result.finalTarget;
          const targetNode = getDeepestTextNode(node);
          if (targetNode) {
            text = targetNode.nodeValue ?? '';
          }
        }
      }

      if (!curSelector || !finalTarget) {
        if (node.tagName.toLowerCase() === 'svg' || node.tagName.toLowerCase() === 'path') {
          // 2. 是否是 svg
          // 如果点击的是 svg 元素，获取其 className
          const { svgClassName: _svgClassname, finalTarget: _finalTarget, text: _text } = checkSVG(node);
          finalTarget = _finalTarget;
          (text = _text), (svgClassName = _svgClassname);
        } else if (node.tagName.toLowerCase() === 'a') {
          // 2. 是否是 a 元素
          const { finalTarget: _finalTarget, text: _text } = checkHTMLTag(node);
          finalTarget = _finalTarget;
          text = _text;
        } else {
          // 3. 是否为 m4b 组件或者子元素
          const { svgClassName: _svgClassname, finalTarget: _finalTarget, text: _text } = checkM4B(node);
          finalTarget = _finalTarget;
          (text = _text), (svgClassName = _svgClassname);
        }
      }

      if ((svgClassName || text || curSelector) && finalTarget) {
        const eleBlockConfig = JSON.parse(findNearestAttribute(node, CONFIG_ATTR) || '{}');
        const { btm } = this.BtmCore!.getFullBtmInfo(node);
        const autoBtm = getAutoBtm(btm);
        const autoDFixedBtm = `${this.BtmCore?.getAId()}.b0.c0.btm_auto_d`;
        const param: any = { ...this.BtmCore?.btmInfo, btm: autoDFixedBtm };
        const { params } = eleBlockConfig;
        const { param: _params, ...blockInfo } = param;
        const parentBCM = findParentWithAttribute(node);
        const starling = getStarlingKey(text, this.BtmCore?.getReverseIndex);
        const eventParams = {
          ...blockInfo,
          btm_block_params: { ...this.BtmCore?.BCMContext, ...parentBCM, ...params },
          position: {
            auto_btm: autoBtm,
            tag: finalTarget?.tagName,
            text,
            'data-uid': getLatestDataUid(node),
            'data-tid': finalTarget?.getAttribute('data-tid'),
            id: finalTarget?.id,
            classname: svgClassName ? svgClassName : getClassName(finalTarget),
            selector: curSelector || getCssSelector(finalTarget as HTMLElement),
            starling_key: starling?.key,
            starling_lang: starling?.lang,
            xpath: getXpath(finalTarget as HTMLElement)
          }
        };
        this.BtmCore?.btmReport({
          eventName: BTMEventType.Click,
          eventParams,
          time: new Date().getTime()
        });
      }
    } catch (e) {
      console.warn(e);
      // noop
    }
  }
}
//# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJtYXBwaW5ncyI6IiIsIm5hbWVzIjpbXSwic291cmNlcyI6W10sInNvdXJjZXNDb250ZW50IjpbXX0=